import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, CheckCircle, CaretRight, XCircle, MinusCircle } from "@phosphor-icons/react";

const COMPLIANCE_OPTS = [
  { value: "compliant", label: "Compliant", color: "bg-green-600 border-green-600", icon: CheckCircle },
  { value: "partial", label: "Partial", color: "bg-amber-500 border-amber-500", icon: MinusCircle },
  { value: "non_compliant", label: "Non-compliant", color: "bg-red-600 border-red-600", icon: XCircle },
];

export default function TaskExecute() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [answers, setAnswers] = useState({});
  const [remarks, setRemarks] = useState({});
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tasks/${id}`).then(({ data }) => {
      setTask(data);
      const a = {}, r = {};
      (data.responses || []).forEach((res) => {
        a[res.question_id] = res.answer;
        r[res.question_id] = res.remarks || "";
      });
      setAnswers(a); setRemarks(r);
    }).catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id]);

  if (!task) return <div className="text-slate-500 text-sm">Loading task…</div>;

  const tatMinutes = task.checklist?.tat_minutes ?? task.area?.sla_minutes ?? 60;

  const start = async () => {
    try {
      await api.post(`/tasks/${id}/start`);
      const { data } = await api.get(`/tasks/${id}`);
      setTask(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setErr(""); setSubmitting(true);
    const responses = (task.checklist?.questions || []).map((q) => ({
      question_id: q.id,
      answer: answers[q.id] ?? "",
      remarks: remarks[q.id] || "",
      image_url: null,
    }));
    for (const q of task.checklist?.questions || []) {
      if (q.mandatory && (answers[q.id] === undefined || answers[q.id] === "")) {
        setErr(`Mandatory question not answered: "${q.text}"`);
        setSubmitting(false); return;
      }
      if (q.remarks_required && !(remarks[q.id] || "").trim()) {
        setErr(`Remarks required for: "${q.text}"`);
        setSubmitting(false); return;
      }
    }
    try {
      await api.post(`/tasks/${id}/submit`, { responses });
      navigate("/worklist");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const canEdit = task.status === "in_progress" || task.status === "rejected";

  return (
    <div className="max-w-3xl space-y-6" data-testid="task-execute-page">
      <Link to="/worklist" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back to worklist
      </Link>

      <header className="flat-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-small text-blue-700">{task.area?.criticality} priority</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
              {task.area?.name}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{task.checklist?.name}</p>
          </div>
          <span className={`status-pill status-${task.status}`}>{task.status.replace("_", " ")}</span>
        </div>
        <div className="divider my-5" />
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div><div className="label-small">Scheduled</div><div className="text-slate-700 mt-1">{new Date(task.scheduled_at).toLocaleString()}</div></div>
          <div><div className="label-small">Due</div><div className="text-slate-700 mt-1">{new Date(task.due_at).toLocaleString()}</div></div>
          <div><div className="label-small">TAT</div><div className="text-slate-700 mt-1">{tatMinutes} min</div></div>
        </div>
      </header>

      {task.status === "pending" && (
        <button onClick={start} data-testid="start-task-button" className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
          Start cleaning task <CaretRight size={14} />
        </button>
      )}

      {(task.status === "in_progress" || task.status === "rejected" || task.status === "completed" || task.status === "verified") && (
        <div className="space-y-4">
          {(task.checklist?.questions || []).map((q, idx) => (
            <div key={q.id} className="flat-card p-5" data-testid={`question-${q.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-xs font-bold text-blue-700">{idx + 1}</div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">{q.text}{q.mandatory && <span className="text-red-600 ml-1">*</span>}</h4>
                      <div className={`label-small mt-1 crit-${q.criticality}`}>{q.criticality}</div>
                    </div>
                  </div>

                  <div className="mt-3">
                    {q.qtype === "compliance" && (
                      <div className="flex flex-wrap gap-2">
                        {COMPLIANCE_OPTS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={!canEdit}
                            data-testid={`answer-${q.id}-${opt.value}`}
                            onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.value }))}
                            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md border transition-colors inline-flex items-center gap-1.5 ${
                              answers[q.id] === opt.value
                                ? `${opt.color} text-white`
                                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                            } disabled:opacity-60`}
                          >
                            <opt.icon size={14} weight="fill" /> {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.qtype === "yes_no" && (
                      <div className="flex gap-2">
                        {["yes", "no"].map((v) => (
                          <button
                            key={v}
                            type="button"
                            disabled={!canEdit}
                            data-testid={`answer-${q.id}-${v}`}
                            onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md border transition-colors ${
                              answers[q.id] === v
                                ? v === "yes" ? "bg-green-600 border-green-600 text-white" : "bg-red-600 border-red-600 text-white"
                                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                            } disabled:opacity-60`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.qtype === "numeric" && (
                      <input
                        type="number"
                        disabled={!canEdit}
                        data-testid={`answer-${q.id}-numeric`}
                        value={answers[q.id] ?? ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        className="w-40 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                        placeholder="0"
                      />
                    )}
                    {q.qtype === "remarks" && (
                      <textarea
                        disabled={!canEdit}
                        data-testid={`answer-${q.id}-remarks`}
                        value={answers[q.id] ?? ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                        placeholder="Type your observation…"
                      />
                    )}

                    {q.qtype !== "remarks" && (
                      <input
                        type="text"
                        disabled={!canEdit}
                        data-testid={`remarks-${q.id}`}
                        value={remarks[q.id] || ""}
                        onChange={(e) => setRemarks((r) => ({ ...r, [q.id]: e.target.value }))}
                        className="w-full mt-2 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                        placeholder={q.remarks_required ? "Remarks required…" : "Optional remarks"}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="task-error">{err}</div>}

          {canEdit && (
            <button onClick={submit} disabled={submitting} data-testid="submit-task-button" className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors disabled:opacity-60">
              <CheckCircle size={16} weight="fill" /> {submitting ? "Submitting…" : "Submit checklist"}
            </button>
          )}
          {task.status === "completed" && (
            <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Awaiting supervisor verification.
            </div>
          )}
          {task.status === "verified" && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              Verified on {new Date(task.verified_at).toLocaleString()}.
            </div>
          )}
          {task.status === "rejected" && task.verification_remarks && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              Rejected by supervisor: {task.verification_remarks}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
